// Katalog sürükle-bırak / ↑↓ sıralama kaydı (Finar rev-2 #4b/#12b/#14, Task 6 R6-1). İyimser:
// yeni sıra hemen yerel listeye uygulanır, sonra TEK istek `POST /kpi/metrics/reorder`
// (`{parentId, orderedIds}` — sunucu tek transaction'da yazar). Hata → yerel liste geri alınır,
// katalog yeniden yüklenir (sunucu gerçeği), kalıcı uyarı bandı. Kayıt sürerken sürükleme pasif.
// Bant: başarıda "Sıra kaydedildi" 4 sn.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { KpiMetric } from "@mahir/shared";
import { reorderKpiMetrics } from "../../api/kpi";
import { applyOrderChanges, type CatalogReorderPlan } from "../../lib/kpi-reorder";
import { KPI_COPY } from "../../lib/kpi-ui";

const SAVED_MS = 4000;

export function useCatalogReorder(opts: {
  metrics: KpiMetric[] | null;
  setMetrics: (next: KpiMetric[]) => void;
  reload: () => Promise<void>;
}): {
  busy: boolean;
  onReorder: (parentId: string | null, plan: CatalogReorderPlan) => void;
  band: ReactNode;
} {
  const { metrics, setMetrics, reload } = opts;
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"saved" | "failed" | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (status !== "saved") return;
    const t = setTimeout(() => setStatus(null), SAVED_MS);
    return () => clearTimeout(t);
  }, [status]);

  const onReorder = useCallback(
    (parentId: string | null, plan: CatalogReorderPlan): void => {
      if (inFlight.current || !metrics || plan.orderedIds.length === 0) return;
      inFlight.current = true;
      const before = metrics;
      setMetrics(applyOrderChanges(before, plan.indexes));
      setBusy(true);
      setStatus(null);
      void (async () => {
        try {
          await reorderKpiMetrics(parentId, plan.orderedIds);
          setStatus("saved");
        } catch {
          setMetrics(before);
          setStatus("failed");
          await reload().catch(() => undefined);
        } finally {
          inFlight.current = false;
          setBusy(false);
        }
      })();
    },
    [metrics, setMetrics, reload],
  );

  const band =
    status === "saved" ? (
      <div
        role="status"
        data-testid="kpi-catalog-reorder-saved"
        className="text-sm rounded-lg px-3 py-2 mb-3"
        style={{
          background: "color-mix(in srgb, var(--color-success) 12%, white)",
          color: "var(--color-success)",
        }}
      >
        {KPI_COPY.reorder.saved}
      </div>
    ) : status === "failed" ? (
      <div
        role="alert"
        data-testid="kpi-catalog-reorder-failed"
        className="text-sm rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
        style={{
          background: "var(--color-warning-bg)",
          color: "var(--color-warning-text)",
          border: "1px solid var(--color-warning-border)",
        }}
      >
        <span className="flex-1">{KPI_COPY.reorder.failed}</span>
        <button
          type="button"
          onClick={() => setStatus(null)}
          style={{ textDecoration: "underline", minHeight: 24 }}
        >
          {KPI_COPY.reorder.dismiss}
        </button>
      </div>
    ) : null;

  return { busy, onReorder, band };
}
